import type { SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import type { ColumnFilterItem, TableQuery, TableQueryWithSearchPhrase } from 'common/models';
import { SortOrder } from 'common/models';
import type { MouseEvent } from 'react';
import { useEffect } from 'react';
import type { NavigateFunction } from 'react-router-dom';

import { getI18nTranslate } from './translationHelper';

export const defaultTablePageSize = 100;

export const defaultTableQuery: Pick<TableQuery, 'page' | 'pageSize'> = {
  page: 1,
  pageSize: defaultTablePageSize
};

export const defaultSelectQuery: Pick<TableQuery, 'page' | 'pageSize'> = {
  page: 1,
  pageSize: 50
};

export const NULL_TABLE_FILTER_VALUE = 'null';

export const getTableFiltersWithNotSet = (
  filters: ColumnFilterItem[],
  notSetText: React.ReactNode
): ColumnFilterItem[] => [
  {
    text: notSetText,
    value: NULL_TABLE_FILTER_VALUE
  },
  ...filters
];

export const sizeChangerPaginationConfig = (): TablePaginationConfig => {
  return {
    showSizeChanger: true,
    locale: { items_per_page: getI18nTranslate('misc.items') },
    showTotal: (total) => getI18nTranslate('misc.totalItems', { total })
  };
};

export interface TableSortOptions<TSortBy extends string | number> {
  sortKeys: readonly TSortBy[];
  defaultSortBy?: NoInfer<TSortBy>;
}

export const isTableSortKey = <TSortBy extends string | number>(
  value: unknown,
  sortKeys: readonly TSortBy[]
): value is TSortBy => sortKeys.some((key) => key === value);

/** Validate sort parameters from an external source, such as a URL or Ant Design. */
export const getTableSortParams = <TSortBy extends string | number>(
  sortBy: unknown,
  sortOrder: unknown,
  sortKeys: readonly TSortBy[]
): Pick<TableQuery<TSortBy>, 'sortBy' | 'sortOrder'> => {
  if (
    isTableSortKey(sortBy, sortKeys) &&
    (sortOrder === SortOrder.Ascending || sortOrder === SortOrder.Descending)
  ) {
    return { sortBy, sortOrder };
  }

  return { sortBy: undefined, sortOrder: undefined };
};

export function getTableQueryFromAntdTable<TSortBy extends string | number, TRecord>(
  pagination: TablePaginationConfig,
  sorter: SorterResult<TRecord> | SorterResult<TRecord>[],
  options: TableSortOptions<TSortBy>
): TableQuery<TSortBy>;
export function getTableQueryFromAntdTable<TRecord>(
  pagination: TablePaginationConfig,
  sorter: SorterResult<TRecord> | SorterResult<TRecord>[],
  defaultSortBy?: string | number
): TableQuery;
export function getTableQueryFromAntdTable<TRecord>(
  pagination: TablePaginationConfig,
  sorter: SorterResult<TRecord> | SorterResult<TRecord>[],
  options?: string | number | TableSortOptions<string | number>
): TableQuery {
  // These queries support a single sort; ignore inactive entries in an AntD sorter array.
  const activeSorter = Array.isArray(sorter) ? sorter.find((item) => item.order) : sorter;
  const columnKey = activeSorter?.columnKey;
  const defaultSortBy = typeof options === 'object' ? options.defaultSortBy : options;
  const sortBy =
    typeof columnKey === 'string' || typeof columnKey === 'number' ? columnKey : defaultSortBy;

  const sortParams =
    typeof options === 'object'
      ? getTableSortParams(sortBy, activeSorter?.order, options.sortKeys)
      : { sortBy, sortOrder: activeSorter?.order as SortOrder };

  return {
    page: pagination.current || 1,
    pageSize: pagination.pageSize || 10,
    ...sortParams
  };
}

export const defaultMaterialTableQuery: Pick<TableQuery, 'page' | 'pageSize'> = {
  page: 1,
  pageSize: 50
};

export const getParamsFromQuery = (query: string) => {
  const params: Record<string, string | number> = {};

  new URLSearchParams(query).forEach((value, key) => {
    if (value) {
      params[key] = value;
    }
  });

  return params;
};

export const getQueryFromParams = (
  params: Record<string, string | number | null | undefined | boolean>
) => {
  return Object.keys(params)
    .reduce((acc: string[], key) => {
      if (params[key]) {
        acc.push(`${key}=${params[key] as string}`);
      }

      return acc;
    }, [])
    .join('&');
};

export const getColumnSortOrder = <TSortBy extends string | number>(
  columnKey: NoInfer<TSortBy>,
  tableQuery: Pick<TableQuery<TSortBy>, 'sortBy' | 'sortOrder'>
) => {
  if (columnKey === tableQuery.sortBy) {
    return tableQuery.sortOrder;
  }

  return undefined;
};

/** Keep the column key and its controlled sort indicator derived from the same key. */
export const getSortableColumnProps = <TSortBy extends string | number>(
  columnKey: NoInfer<TSortBy>,
  tableQuery: Pick<TableQuery<TSortBy>, 'sortBy' | 'sortOrder'>
) => ({
  key: columnKey,
  sorter: true as const,
  sortOrder: getColumnSortOrder(columnKey, tableQuery) ?? null
});

export const getTableQueryKey = (
  name: string,
  params: TableQueryWithSearchPhrase,
  id?: string,
  infinite?: boolean
) => {
  const typedParams = params as any as Record<string, string>;

  const keys = [name, ...getParamsAsKeys(typedParams)];

  if (id) {
    keys.splice(1, 0, id);
  }

  if (infinite) {
    keys.push('infiniteQuery');
  }

  return keys;
};

export const getParamsAsKeys = (params: Record<string, string>) =>
  Object.keys(params)
    .map((key) => params[key])
    .filter((x) => x !== undefined && x !== null);

const getActivePageScrollContainer = () => {
  return (
    document.querySelector<HTMLElement>('.PageContent') ||
    document.querySelector<HTMLElement>('.PageContentWrapper')
  );
};

const getPageScrollTop = () => {
  const container = getActivePageScrollContainer();

  return container?.scrollTop ?? window.scrollY;
};

/**
 * Restores previously saved vertical scroll position for list pages on mount.
 *
 * Scroll position must be saved before navigation, for example by using
 * getTableOnRowProps(path, navigate, scrollKey).
 *
 * Polls via requestAnimationFrame until the scroll container has enough rendered
 * content to reach the saved position (handles async table data loading).
 *
 * Example:
 * useScrollRestoration('serviceobjects');
 */
export const useScrollRestoration = (key: string): void => {
  useEffect(() => {
    const storageKey = `scroll-${key}`;
    const saved = sessionStorage.getItem(storageKey);

    if (saved === null) {
      return;
    }

    const wantedScrollTop = Number(saved);

    if (Number.isNaN(wantedScrollTop)) {
      sessionStorage.removeItem(storageKey);
      return;
    }

    sessionStorage.removeItem(storageKey);

    if (wantedScrollTop === 0) {
      return;
    }

    let rafId: number;
    let attempts = 0;
    const maxAttempts = 30; // ~500 ms at 60 fps

    const tryRestore = () => {
      attempts++;
      const container = getActivePageScrollContainer();

      if (container) {
        const scrollable = container.scrollHeight - container.clientHeight;

        if (scrollable >= wantedScrollTop || attempts >= maxAttempts) {
          container.scrollTop = wantedScrollTop;
          return;
        }
      } else if (attempts >= maxAttempts) {
        window.scrollTo(0, wantedScrollTop);
        return;
      }

      rafId = requestAnimationFrame(tryRestore);
    };

    rafId = requestAnimationFrame(tryRestore);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [key]);
};

const persistPageContentScroll = (key: string) => {
  const storageKey = `scroll-${key}`;
  const scrollTop = getPageScrollTop();

  sessionStorage.setItem(storageKey, String(scrollTop));
};

/**
 * Row navigation helper for AntD Table:
 * - normal click: SPA navigate
 * - Ctrl/Cmd click: open in new tab
 * - optional scrollKey: saves current list scroll before navigation
 * - pass null as path to disable navigation for that row (onClick is no-op)
 *
 * Example:
 * onRow={(record) => getTableOnRowProps(`/orders/${record.id}`, navigate, 'orders')}
 * onRow={(record) => getTableOnRowProps(record.interventionId ? `/orders/${record.orderId}/interventions/${record.interventionId}` : null, navigate)}
 */
export const getTableOnRowProps = (
  path: string | null,
  navigate: NavigateFunction,
  scrollKey?: string
) => ({
  onClick: (e: MouseEvent<HTMLElement>) => {
    if (!path) {
      return;
    }

    if (scrollKey) {
      persistPageContentScroll(scrollKey);
    }

    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(path);
  }
});
